import { useEffect, useState, useRef } from "react";

import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ReactSVG } from "react-svg";

import Steps from "../../Steps";
import Button from "../../../../common/Button";
import ChartNav from "../../../../common/ChartNav";
import Medications from "../../medications/medications";
import {
  updateClinicalService,
  updateMedicationReview,
} from "../../../../../redux/modules/clinical-services/actions";
import Alert from "../../../../common/Alert";
import { ClinicalServiceTypes, getValidStep } from "../../../text/data";
import { ConditionReasons } from "./data";
import SubNavigation from "../../common/sub-navigation";
import VaccineCards from "../../service-steps/health-wellness/vaccine-cards";
import FollowUpCard from "./follow-up-appointment";
import VaccineBookingPopup from "./vaccine-booking-popup";
import VaccineHealthCard from "./vaccine-followup-appointment";
import ThirdCardFollowUp from "./medication-service-follow-up";
import useFollowUpSuppression from "../../../../../hooks/useFollowUpSuppression";

const EMPTY_EDUCATION = {};
const EDUCATION_STEP_NAME = "Education";
const EDUCATION_REVIEW_SUBSTEP_INDEX = 0;
const EDUCATION_FOLLOW_UP_SUBSTEP = 2;
const EDUCATION_REQUIRED_REASON_GROUPS = [
  ConditionReasons.bloodPressure,
  ConditionReasons.cholesterol,
];

const getMedicationReasons = (clinicalServiceObj) =>
  clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.medications?.medications?.map(
    (item) => item?.reasonForMedicationName
  ) || [];

const shouldSkipEducationReview = (medicationReasons = []) => {
  const reasonSet = new Set(medicationReasons.filter(Boolean));
  const hasRequiredReason = EDUCATION_REQUIRED_REASON_GROUPS.some((group) =>
    group.some((reason) => reasonSet.has(reason))
  );
  return !hasRequiredReason;
};

const hideEducationReviewSubStep = (steps = []) =>
  steps.map((step) =>
    step?.name === EDUCATION_STEP_NAME
      ? {
          ...step,
          subSteps: (step.subSteps || []).map((sub, index) =>
            index === EDUCATION_REVIEW_SUBSTEP_INDEX ? { ...sub, show: false } : sub
          ),
        }
      : step
  );

const FollowUp = () => {
  const { clinicalServiceObj, clinicalServiceName, clinicalServiceSteps } =
    useSelector((state) => state.clinicalService);
  const educationFromStore = 
  useSelector( (state) => state.clinicalService.clinicalServiceObj.initialAssessment.initialAssessmentForm.education);
  const education = educationFromStore || EMPTY_EDUCATION;
  const dispatch = useDispatch();
  const { serviceId } = useParams();
  const { currentStep, currentSubStep } =
    clinicalServiceObj.initialAssessment.initialAssessmentForm;

  const [MRDate, setMRDate] = useState(null);
  const [selectedCard, setSelectedCard] = useState(0);
  const [followUpTitle, setFollowUpTitle] = useState(null);
  const [navigateToVaccinePage, setNavigateToVaccinePage] = useState(false);
  const [showAlert, setShowAlert] = useState(true);

  // Follow-up data comes from redux (single source of truth)
  const followUpDate = education?.followUpDate ?? null;
  const reasonStart = education?.followUp ?? null;
  const explaination = education?.otherFollowUpReason ?? null;
  const followUpType = Array.isArray(education?.followUpType)
    ? education.followUpType
    : [];
  const [showARResultCard, setShowARResultCard] = useState(false);

  // New state for managing the card flip delay (1st card)
  const [isFlipping, setIsFlipping] = useState(false);
  const [isCardFlipped, setIsCardFlipped] = useState(false);
  const [isEditingFollowUp, setIsEditingFollowUp] = useState(false);
  const userHiddenFollowUpRef = useRef(false);
  const pendingFollowUpClearRef = useRef(false);
  /** True while the follow-up card is in Edit (⋯ menu → Edit); Next stays off until Update or Cancel. */
  const [followUpEditOpen, setFollowUpEditOpen] = useState(false);

  // vaccine appointment (2nd card)
  const [selectedVaccinesForAppt, setSelectedVaccinesForAppt] = useState([]); // selected vaccines and went for booking
  const [apptBookedVaccines, setApptBookedVaccines] = useState([]); //selected vaccines whose appointment are also booked

  // show the booked(flipped) card or review vaccine health button side
  const [vaccineResultCard, setVaccineResultCard] = useState(false);
  const [vaccineApptBooked, setVaccineApptBooked] = useState(false);

  const [showVaccineBookingPopup, setShowVaccineBookingPopup] = useState(false); // show the vaccine booking popup

  // track if component just mounted/navigated to
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [comingFromEdit, setComingFromEdit] = useState(false);

  const [isOverflowing, setIsOverflowing] = useState(false);
  const [enableLeftArrow, setEnableLeftArrow] = useState(false);
  const [enableRightArrow, setEnableRightArrow] = useState(false);
  const justSavedVaccinesRef = useRef(false);

  useEffect(() => {
    const el = document.getElementById("follow-up");
    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
      setEnableRightArrow(true);
    }
  }, [clinicalServiceName]);

  useEffect(() => {
    if (isInitialLoad) setIsInitialLoad(false);
  }, [isInitialLoad]);

  useEffect(() => {
    setIsInitialLoad(true);
  }, [currentStep]);

  const [isFollowUpSuppressed, setSuppression] = useFollowUpSuppression(serviceId);
  const [isVaccineSuppressed, setVaccineSuppression] = useFollowUpSuppression(
    serviceId,
    "hc-vaccine-suppressed"
  );

  const shouldSuppressFollowUp =
    pendingFollowUpClearRef.current || isFollowUpSuppressed;
  const shouldSuppressVaccine = isVaccineSuppressed;
  // Use empty string for date when suppressed so the datetime input shows its placeholder
  const effectiveFollowUpDate = shouldSuppressFollowUp ? "" : followUpDate;
  const effectiveReasonStart = shouldSuppressFollowUp ? null : reasonStart;
  const effectiveExplaination = shouldSuppressFollowUp ? "" : explaination;
  const normalizeVaccineName = (v) => {
    if (!v?.name) return '';
    const n = v.name;
    if (typeof n === 'string') return n;
    if (typeof n !== 'object') return '';
    const paths = [n.name, n.value, n.label, n.name?.name, n.name?.value, n.name?.label];
    return paths.find(p => typeof p === 'string' && p) || Object.values(n).find(vv => typeof vv === 'string' && vv) || '';
  };
  const normalizeVaccines = (vaccines) => 
    (vaccines && Array.isArray(vaccines)) ? vaccines.map((v) => ({ ...v, name: normalizeVaccineName(v) })) : [];
  const effectiveVaccineSchedule = shouldSuppressVaccine
    ? []
    : normalizeVaccines(selectedVaccinesForAppt);
  const effectiveVaccineBooked = shouldSuppressVaccine ? false : vaccineApptBooked;
  const effectiveBookedVaccines = shouldSuppressVaccine ? [] : apptBookedVaccines;

  const buildEducationPayload = (overrides = {}) => ({
    ...education,
    followUp: effectiveReasonStart,
    followUpDate: effectiveFollowUpDate,
    otherFollowUpReason: effectiveExplaination,
    followUpType,
    vaccineSchedule: effectiveVaccineSchedule,
    appointmentBooked: effectiveVaccineBooked,
    // If there's a pending user-clear, enforce cleared follow-up fields until redux confirms
    ...(shouldSuppressFollowUp
      ? { followUp: null, followUpDate: null, otherFollowUpReason: null, followUpType: [] }
      : {}),
    ...overrides,
  });

  const ensureConsistentDateFormat = (dv) => {
    if (!dv) return null;
    if (typeof dv === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dv)) return dv;
    if (typeof dv === "string" && /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}$/.test(dv)) return dv.replace(/\s+/, "T");
    if (typeof dv === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dv)) return `${dv}T12:00`;
    if (dv instanceof Date) {
      const pad = (n) => n.toString().padStart(2, "0");
      return `${dv.getFullYear()}-${pad(dv.getMonth() + 1)}-${pad(dv.getDate())}T${pad(dv.getHours())}:${pad(dv.getMinutes())}`;
    }
    return dv;
  };

  const hasBookedVaccineAppointments = (vaccines = []) =>
    normalizeVaccines(vaccines).some((v) =>
      Boolean(ensureConsistentDateFormat(v?.bookingDate))
    );

  const getVaccineStorageKey = () => serviceId ? `hc-vaccine-schedule-${serviceId}` : null;

  const readPersistedVaccines = () => {
    if (typeof window === "undefined") return [];
    const key = getVaccineStorageKey();
    if (!key) return [];
    const stored = window.localStorage.getItem(key);
    return normalizeVaccines(stored ? JSON.parse(stored) : []);
  };

  const persistVaccinesLocally = (vaccines) => {
    if (typeof window === "undefined") return;
    const key = getVaccineStorageKey();
    if (!key) return;
    if (!vaccines || vaccines.length === 0) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, JSON.stringify(vaccines));
  };
 

  const processVaccinesFromRedux = (reduxVaccines, currentVaccines, cachedVaccines = []) => {
    const sources = [currentVaccines, cachedVaccines].filter(Boolean);
    const findMatchedVaccine = (v) => {
      const vName = normalizeVaccineName(v);
      for (const s of sources) {
        const match = s?.find((sv) => {
          const svName = normalizeVaccineName(sv);
          return (sv.id && v.id && sv.id === v.id) || (!sv.id && !v.id && svName === vName);
        });
        if (match) return match;
      }
      return null;
    };
    const findMatchWithTime = (v) => {
      const m = findMatchedVaccine(v);
      if (m?.bookingDate?.includes("T")) return m.bookingDate.split("T")[1];
      return null;
    };

    return reduxVaccines.map((v) => {
      const matched = findMatchedVaccine(v);
      let n = normalizeVaccineName(v);
      if (!n || n === '[object Object]') {
        for (const k in v) {
          if (k !== 'name' && typeof v[k] === 'object' && v[k]?.name && typeof v[k].name === 'string' && v[k].name !== '[object Object]') {
            n = v[k].name;
            break;
          }
        }
      }
      let bd = v.bookingDate;
      const merged = {
        ...matched,
        ...v,
        name: n,
        vaccineDoseType: v?.vaccineDoseType ?? matched?.vaccineDoseType,
        totalNumberOfVaccineDoses:
          v?.totalNumberOfVaccineDoses ?? matched?.totalNumberOfVaccineDoses,
        seriesCountForPopup:
          v?.seriesCountForPopup ?? matched?.seriesCountForPopup,
        seriesLabelsForPopup:
          v?.seriesLabelsForPopup ?? matched?.seriesLabelsForPopup,
        selectedBoosterOrdinals:
          v?.selectedBoosterOrdinals ?? matched?.selectedBoosterOrdinals,
      };
      if (bd?.includes("T")) return { ...merged, bookingDate: bd };
      if (bd && /^\d{4}-\d{2}-\d{2}$/.test(bd)) {
        const pt = findMatchWithTime(v);
        return { ...merged, bookingDate: pt ? `${bd}T${pt}` : `${bd}T12:00` };
      }
      return { ...merged, bookingDate: ensureConsistentDateFormat(bd) || "" };
    });
  };

  const areVaccineArraysDifferent = (current, updated) => {
    const sig = (arr) =>
      arr
        .map((v) => {
          const labels = Array.isArray(v.seriesLabelsForPopup)
            ? v.seriesLabelsForPopup.join(",")
            : "";
          const ordinals = Array.isArray(v.selectedBoosterOrdinals)
            ? v.selectedBoosterOrdinals.join(",")
            : "";
          return [
            v.id || v.name,
            v.bookingDate || "",
            v.vaccineDoseType || "",
            v.appointmentDoseType || "",
            v.totalNumberOfVaccineDoses || "",
            v.seriesCountForPopup || "",
            labels,
            ordinals,
          ].join(":");
        })
        .sort()
        .join("|");
    return sig(current) !== sig(updated);
  };

  const updateScrollArrows = (el) => {
    setEnableRightArrow(Math.ceil(el.scrollLeft) + el.clientWidth !== el.scrollWidth);
    setEnableLeftArrow(el.scrollLeft !== 0);
  };

  const handleLeftScroll = () => {
    const el = document.getElementById("follow-up");
    el.scrollLeft -= 400;
    setTimeout(() => updateScrollArrows(el), 500);
  };

  const handleRightScroll = () => {
    const el = document.getElementById("follow-up");
    el.scrollLeft += 400;
    setTimeout(() => updateScrollArrows(el), 500);
  };

  useEffect(() => {
    setFollowUpTitle(clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis ? "Allergic Rhinitis" : "Medication Review");
  }, [clinicalServiceName]);

  // Hide follow-up card UI flags when redux follow-up data is cleared.
  useEffect(() => {
    const reduxEducation =
      clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.education;
    if (!reduxEducation?.followUpDate && !reduxEducation?.followUp) {
      setShowARResultCard(false);
      setIsCardFlipped(false);
      // Reset any user-hide suppression when redux confirms cleared state
      userHiddenFollowUpRef.current = false;
      // Reset pending clear once redux confirms cleared
      pendingFollowUpClearRef.current = false;
      setSuppression(false);
    }
  }, [
    clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.education,
  ]);

  // Show follow-up result card when redux has follow-up data (e.g., after booking)
  useEffect(() => {
    const reduxEducation =
      clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.education;
    // If the user has deliberately hidden the result card, do not auto-show
    if (userHiddenFollowUpRef.current || shouldSuppressFollowUp) return;

    if (reduxEducation?.followUpDate || reduxEducation?.followUp) {
      setShowARResultCard(true);
      if (reduxEducation?.followUpDate) setIsCardFlipped(true);
    }
  }, [
    clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.education,
  ]);

  // Restore vaccine-related state from redux.
  useEffect(() => {
    const obj = clinicalServiceObj.initialAssessment.initialAssessmentForm.education;
    if (
      clinicalServiceObj.initialAssessment.initialAssessmentForm === undefined ||
      obj === null ||
      obj === undefined
    ) {
      return;
    }

    if (shouldSuppressVaccine) {
      setApptBookedVaccines([]);
      setSelectedVaccinesForAppt([]);
      setVaccineResultCard(false);
      setVaccineApptBooked(false);
      persistVaccinesLocally([]);
      return;
    }

    if (obj.vaccineSchedule && !justSavedVaccinesRef.current) {
      const cachedVaccines = readPersistedVaccines();
      const consistentVaccines = processVaccinesFromRedux(
        obj.vaccineSchedule,
        apptBookedVaccines,
        cachedVaccines
      );

      if (areVaccineArraysDifferent(apptBookedVaccines, consistentVaccines)) {
        const normalizedVaccines = normalizeVaccines(consistentVaccines);
        setApptBookedVaccines(normalizedVaccines);
        setSelectedVaccinesForAppt(normalizedVaccines);
        persistVaccinesLocally(normalizedVaccines);
      }
    } else if (!justSavedVaccinesRef.current && (!obj.vaccineSchedule || obj.vaccineSchedule.length === 0)) {
      persistVaccinesLocally([]);
    }

    if (obj?.vaccineSchedule !== undefined) {
      const hasBookedAppointments = hasBookedVaccineAppointments(obj.vaccineSchedule || []);
      setVaccineResultCard(hasBookedAppointments);
      setVaccineApptBooked(hasBookedAppointments);
      if (!obj.vaccineSchedule || obj.vaccineSchedule.length === 0) {
        setVaccineSuppression(false);
      }
    }
  }, [
    clinicalServiceObj.initialAssessment.initialAssessmentForm.education,
    clinicalServiceName,
    serviceId,
    isInitialLoad,
    shouldSuppressVaccine,
  ]);

  const dispatchUpdate = (updatedSteps) => {
    const action = clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis ? updateClinicalService : updateMedicationReview;
    dispatch(action(serviceId, updatedSteps));
  };

  const onNext = () => {
    if (currentStep <= clinicalServiceSteps.length) {
      const { nextStep, nextSubStep } = getValidStep(clinicalServiceSteps, currentStep, currentSubStep, 1);
      dispatchUpdate({
        education: buildEducationPayload(),
        currentStep: nextStep, currentSubStep: nextSubStep, serviceSteps: clinicalServiceSteps,
      });
    }
  };

  const saveProgress = () => {
    const finalFollowUp = effectiveFollowUpDate ? null : effectiveReasonStart;
    const finalFollowUpDate = (effectiveReasonStart && effectiveReasonStart !== "Pharmacist") ? null : effectiveFollowUpDate;
    const finalExplanation = effectiveFollowUpDate ? null : effectiveExplaination;
    dispatchUpdate({
      education: buildEducationPayload({
        followUp: finalFollowUp,
        followUpDate: finalFollowUpDate,
        otherFollowUpReason: finalExplanation,
      }),
      currentStep, currentSubStep, serviceSteps: clinicalServiceSteps,
    });
  };

  const halfBorderSection = (cardData) => {
    const borderClass = selectedCard === cardData ? "border-REELTeal" : "border-blue-200";
    return (
      <>
        <div className={`absolute inset-0 border-t-0 border-l-0 border-r-[6px] border-b-[6px] top-[50%] left-[50%] border-dashed rounded-bl-[0] rounded-b-[12px] z-0 ${borderClass}`}></div>
        <div className={`absolute inset-0 border-t-0 border-l-0 border-r-[6px] border-b-0 rounded-t-[4px] bottom-[50%] z-0 ${borderClass}`}></div>
        <div className={`absolute inset-0 border-t-0 border-l-0 border-b-[6px] right-[50%] rounded-l-[4px] z-0 ${borderClass}`}></div>
      </>
    );
  };

  const onPrevious = () => {
    const currentStepConfig = clinicalServiceSteps?.[currentStep - 1];
    const isEducationStep = currentStepConfig?.name === EDUCATION_STEP_NAME;
    const isEducationReviewSubStepHidden =
      currentStepConfig?.subSteps?.[EDUCATION_REVIEW_SUBSTEP_INDEX]?.show === false;
    const medicationReasons = getMedicationReasons(clinicalServiceObj);
    const shouldSkipEducationReviewSubStep = shouldSkipEducationReview(medicationReasons);

    const stepsForBackNavigation =
      isEducationStep && shouldSkipEducationReviewSubStep
        ? hideEducationReviewSubStep(clinicalServiceSteps)
        : clinicalServiceSteps;

    // If Education substep-1 is skipped, Back from Follow Up (substep-2)
    // should jump to the previous valid screen before Education.
    const backFromSubStep =
      isEducationStep &&
      currentSubStep === EDUCATION_FOLLOW_UP_SUBSTEP &&
      (isEducationReviewSubStepHidden || shouldSkipEducationReviewSubStep)
        ? 1
        : currentSubStep;

    const { nextStep, nextSubStep } = getValidStep(
      stepsForBackNavigation,
      currentStep,
      backFromSubStep,
      -1
    );
    dispatchUpdate({
      currentStep: nextStep,
      currentSubStep: nextSubStep,
      serviceSteps: stepsForBackNavigation,
    });
  };

  const isNextEnabled = () => {
    if (clinicalServiceName !== ClinicalServiceTypes.AllergicRhinitis) {
      return effectiveFollowUpDate !== null;
    }
    const hasData =
      (effectiveFollowUpDate !== null && effectiveFollowUpDate !== "") ||
      (effectiveReasonStart !== null &&
        effectiveReasonStart !== "" &&
        effectiveReasonStart !== "Pharmacist");
    return hasData && !followUpEditOpen;
  };

  const handleFollowUpEditStart = () => {
    setFollowUpEditOpen(true);
  };

  const handleFollowUpEditComplete = (hasSaved) => {
    setFollowUpEditOpen(false);
    if (hasSaved) {
      saveProgress();
    }
  };

  const handleFollowUpEditCancel = () => {
    setFollowUpEditOpen(false);
    // Same pattern as clearFollowUpSelection: suppress UI + pending clear so the booked
    // card does not reappear from stale Redux/API, and datetime/reason show empty until Redux confirms.
    userHiddenFollowUpRef.current = true;
    pendingFollowUpClearRef.current = true;
    setSuppression(true);
    dispatchUpdate({
      education: buildEducationPayload({
        followUp: null,
        followUpDate: null,
        otherFollowUpReason: null,
        followUpType: [],
      }),
      currentStep,
      currentSubStep,
      serviceSteps: clinicalServiceSteps,
    });
    setShowARResultCard(false);
    setIsCardFlipped(false);
  };

  const shouldShowFlipArrow = () => 
    clinicalServiceName !== ClinicalServiceTypes.AllergicRhinitis && effectiveFollowUpDate !== null && followUpType.length > 0;

  const triggerFlip = (delay = 2000) => {
    setIsFlipping(true);
    setTimeout(() => setIsFlipping(false), delay);
  };

  const handleReasonStartChange = (val) => {
    const nextDate = val !== "Pharmacist" ? null : effectiveFollowUpDate;
    dispatchUpdate({
      education: buildEducationPayload({
        followUp: val,
        followUpDate: nextDate,
        otherFollowUpReason: val === "Other" ? effectiveExplaination : null,
      }),
      currentStep,
      currentSubStep,
      serviceSteps: clinicalServiceSteps,
    });
    // Clear any user-hidden suppression / pending clear when user makes a new selection
    userHiddenFollowUpRef.current = false;
    pendingFollowUpClearRef.current = false;
    setSuppression(false);
    // Show result card for follow-up selections (no date case shows without flip)
    setShowARResultCard(true);
    if (nextDate) setIsCardFlipped(true);
    // For AllergicRhinitis: keep form open when "Other" is selected, auto-save for other options
    if (clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis) {
      setIsEditingFollowUp(val === "Other");
      if (val !== "Other") triggerFlip();
    }
  };

  const handleExplainationChange = (val) => {
    dispatchUpdate({
      education: buildEducationPayload({ otherFollowUpReason: val }),
      currentStep,
      currentSubStep,
      serviceSteps: clinicalServiceSteps,
    });
    // Clear suppression / pending clear when user edits explanation
    userHiddenFollowUpRef.current = false;
    pendingFollowUpClearRef.current = false;
    setSuppression(false);
    // For AllergicRhinitis: auto-save when explanation is provided for "Other", or for non-Other options
    if (clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis) {
      const shouldSave = effectiveReasonStart === "Other" ? (val && val.trim() !== "") : true;
      if (shouldSave) {
        setIsEditingFollowUp(false);
        // show result card after saving explanation
        setShowARResultCard(true);
        // Use longer delay when saving "Other" explanation to allow user to finish typing
        triggerFlip(effectiveReasonStart === "Other" ? 5000 : 2000);
      }
    }
  };

  const handleDateChange = (date) => {
    dispatchUpdate({
      education: buildEducationPayload({
        followUpDate: date,
        followUp: date ? null : effectiveReasonStart,
        otherFollowUpReason: date ? null : effectiveExplaination,
      }),
      currentStep,
      currentSubStep,
      serviceSteps: clinicalServiceSteps,
    });
    setIsEditingFollowUp(false);
    // Clear any user-hidden suppression and pending clear, show the booked card immediately
    userHiddenFollowUpRef.current = false;
    pendingFollowUpClearRef.current = false;
    setSuppression(false);
    setShowARResultCard(true);
    if (date) setIsCardFlipped(true);
    if (clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis && date) triggerFlip();
  };

  const isDropdownDisabled = () => 
    clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis && effectiveFollowUpDate !== null && effectiveFollowUpDate !== "";

  const clearFollowUpSelection = () => {
    setIsEditingFollowUp(false);
    setFollowUpEditOpen(false);
    // Mark that user intentionally hid the follow-up so Redux-driven effects don't re-show immediately
    userHiddenFollowUpRef.current = true;
    // Mark pending clear so any subsequent dispatches include cleared follow-up fields
    pendingFollowUpClearRef.current = true;
    setSuppression(true);
    const payload = {
      education: buildEducationPayload({
        followUp: null,
        followUpDate: null,
        otherFollowUpReason: null,
        followUpType: [],
      }),
      currentStep,
      currentSubStep,
      serviceSteps: clinicalServiceSteps,
    };
    dispatchUpdate(payload);
  };

  const restoreFollowUpSelection = (values) => {
    const hasValues = Boolean(values?.date || values?.reason);
    if (hasValues) {
      setSuppression(false);
      pendingFollowUpClearRef.current = false;
      userHiddenFollowUpRef.current = false;
    }
    dispatchUpdate({
      education: buildEducationPayload({
        followUpDate: values?.date ?? null,
        followUp: values?.reason ?? null,
        otherFollowUpReason: values?.explanation ?? null,
        followUpType: values?.followUpType || [],
      }),
      currentStep,
      currentSubStep,
      serviceSteps: clinicalServiceSteps,
    });
  };

  const handleDeleteAllVaccines = () => {
    setVaccineSuppression(true);
    setApptBookedVaccines([]);
    setSelectedVaccinesForAppt([]);
    setVaccineResultCard(false);
    setVaccineApptBooked(false);
    persistVaccinesLocally([]);

    dispatchUpdate({
      education: buildEducationPayload({
        vaccineSchedule: [],
        appointmentBooked: false,
      }),
      currentStep,
      currentSubStep,
      serviceSteps: clinicalServiceSteps,
    });
  };

  const handleArrowClick = (e) => {
    e.stopPropagation();
    setIsFlipping(true);
    setTimeout(() => { setIsFlipping(false); setIsCardFlipped(true); }, 100);
  };

  const handleAppointmentBook = (selectedVaccines) => {
    // Prevent immediate redux rehydrate from overwriting fresh row metadata
    // (series labels/count, booster ordinals) coming from VaccineCards.
    justSavedVaccinesRef.current = true;

    const map = new Map();
    apptBookedVaccines.forEach((v) => map.set(normalizeVaccineName(v), v.bookingDate));
    const scheduledVaccineDetails = new Map(
      (education?.vaccineSchedule || []).map((vaccine) => [
        normalizeVaccineName(vaccine),
        vaccine,
      ])
    );
    const updated = selectedVaccines.map((selectedVaccine) => {
      const selectedVaccineDetails =
        typeof selectedVaccine === "object" && selectedVaccine !== null
          ? selectedVaccine
          : {};
      const name =
        typeof selectedVaccine === "string"
          ? selectedVaccine
          : normalizeVaccineName(selectedVaccineDetails);
      const existingDetails = scheduledVaccineDetails.get(name) || {};
      const hasFreshDoseSelection =
        Boolean(selectedVaccineDetails.vaccineDoseType) ||
        Boolean(selectedVaccineDetails.totalNumberOfVaccineDoses) ||
        (Array.isArray(selectedVaccineDetails.selectedBoosterOrdinals) &&
          selectedVaccineDetails.selectedBoosterOrdinals.length > 0);
      const staleRowFields = hasFreshDoseSelection
        ? {
            seriesNumber: undefined,
            appointmentDoseType: undefined,
            parentVaccineName: undefined,
          }
        : {};
      return {
        ...existingDetails,
        ...staleRowFields,
        ...selectedVaccineDetails,
        name,
        bookingDate:
          map.get(name) ||
          selectedVaccineDetails.bookingDate ||
          existingDetails.bookingDate ||
          null,
      };
    });
    const normalized = normalizeVaccines(updated);
    setSelectedVaccinesForAppt(normalized);
    setApptBookedVaccines(normalized);
    persistVaccinesLocally(normalized);

    setTimeout(() => {
      justSavedVaccinesRef.current = false;
    }, 1200);

    if (selectedVaccines.length === 0) {
      setShowVaccineBookingPopup(false);
      setVaccineResultCard(false);
      setVaccineApptBooked(false);
    } else {
      setVaccineSuppression(false);
      // AMR confirmation means "selected for booking", not "already booked".
      setShowVaccineBookingPopup(false);
      setVaccineResultCard(false);
      setVaccineApptBooked(false);
    }
    setComingFromEdit(false);
    setNavigateToVaccinePage(false);
  };

  return navigateToVaccinePage ? (
    <VaccineCards showBack={true} onContinue={handleAppointmentBook} />
  ) : (
    <div className=" flex flex-col flex-1 w-full gap-4 bg-white">
      <div className="flex flex-col flex-1">
        <div className="flex flex-1 gap-4">
          <ChartNav />
          <Medications />
          <div className="flex flex-1 w-full h-[668px] overflow-y-auto scroll-smooth ">
            <div className="w-full gap-5">
              <div className="flex items-center justify-between mt-0.5 mb-0.5">
                <h4 className="text-xs-display font-bold text-teal-300">
                  Follow Up - Appointments{" "}
                  {clinicalServiceName !== ClinicalServiceTypes.AllergicRhinitis
                    ? "(Optional)"
                    : ""}
                </h4>

                <SubNavigation />
              </div>
              <div className="border-b-[7px] border-gray-20 mb-[20px]"></div>
              {showAlert && (
                <Alert
                  title="Alert"
                  type="primary"
                  className="mb-4"
                  hideCross={true}
                  showTexture={false}
                  content={() => (
                    <p>
                      <b>Scheduler Coming Soon to REEL!</b> Use the calendar
                      link to book appointments & reminders will be added to the
                      Storyboard.
                    </p>
                  )}
                  handleClose={() => setShowAlert(false)}
                />
              )}
              <div
                id="follow-up"
                className="flex flex-col w-[100%] overflow-auto"
              >
                <div className="flex flex-col justify-between gap-6">
                  <div className="flex flex-row gap-12">
                    {/*First card follow up*/}
                    <FollowUpCard
                      selectedCard={selectedCard}
                      setSelectedCard={setSelectedCard}
                      followUpTitle={followUpTitle}
                      clinicalServiceName={clinicalServiceName}
                        followUpDate={effectiveFollowUpDate}
                        reasonStart={effectiveReasonStart}
                        explaination={effectiveExplaination}
                      followUpType={followUpType}
                      showARResultCard={showARResultCard}
                      isFlipping={isFlipping}
                      isCardFlipped={isCardFlipped}
                      shouldShowFlipArrow={shouldShowFlipArrow}
                      handleArrowClick={handleArrowClick}
                      handleDateChange={handleDateChange}
                      handleReasonStartChange={handleReasonStartChange}
                      handleExplainationChange={handleExplainationChange}
                      isDropdownDisabled={isDropdownDisabled}
                      setShowARResultCard={setShowARResultCard}
                      setIsCardFlipped={setIsCardFlipped}
                      clearFollowUpSelection={clearFollowUpSelection}
                      restoreFollowUpSelection={restoreFollowUpSelection}
                      onFollowUpEditStart={handleFollowUpEditStart}
                      onFollowUpEditComplete={handleFollowUpEditComplete}
                      onFollowUpEditCancel={handleFollowUpEditCancel}
                      halfBorderSection={halfBorderSection}
                    />
                    {/*Second card follow up*/}
                    <VaccineHealthCard
                      selectedCard={selectedCard}
                      setSelectedCard={setSelectedCard}
                      vaccineResultCard={vaccineResultCard}
                      setVaccineResultCard={setVaccineResultCard}
                      setVaccineApptBooked={setVaccineApptBooked}
                      setNavigateToVaccinePage={setNavigateToVaccinePage}
                      apptBookedVaccines={effectiveBookedVaccines}
                      setApptBookedVaccines={setApptBookedVaccines}
                      saveProgress={saveProgress}
                      onDeleteAllVaccines={handleDeleteAllVaccines}
                      setShowVaccineBookingPopup={setShowVaccineBookingPopup}
                      setComingFromEdit={setComingFromEdit}
                      halfBorderSection={halfBorderSection}
                      setSelectedVaccinesForAppt={selectedVaccinesForAppt}
                      isMA={
                        clinicalServiceName ===
                        ClinicalServiceTypes.AllergicRhinitis
                      }
                      cardIndex={2}
                    />
                    {/*Third card follow up*/}
                    {(clinicalServiceName ===
                      ClinicalServiceTypes.AllergicRhinitis ||
                      clinicalServiceName ===
                        ClinicalServiceTypes.MedicationReview) && (
                      <ThirdCardFollowUp
                        selectedCard={selectedCard}
                        setSelectedCard={setSelectedCard}
                        clinicalServiceName={clinicalServiceName}
                        MRDate={MRDate}
                        setMRDate={setMRDate}
                        halfBorderSection={halfBorderSection}
                        cardIndex={3}
                      />
                    )}
                  </div>
                  {isOverflowing && (
                    <>
                      {enableLeftArrow && (
                        <button
                          className="absolute m-auto bg-gray-20 px-3 py-[18px] top-0 bottom-0 h-fit text-teal-600 font-bold z-10 shadow-lg"
                          onClick={() => handleLeftScroll()}
                        >
                          <ReactSVG
                            src="/icons/arrow-right.svg"
                            className="w-[15px] rotate-180"
                          />
                        </button>
                      )}
                      {enableRightArrow && (
                        <button
                          className="absolute m-auto right-0 bg-gray-20 px-3 py-[18px] top-0 bottom-0 h-fit text-teal-600 font-bold z-10 shadow-lg"
                          onClick={() => handleRightScroll()}
                        >
                          <ReactSVG
                            src="/icons/arrow-right.svg"
                            className="w-[15px]"
                          />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between space-x-4 rounded">
        <div className="w-full ">
          <Steps position="bottom" stepsList={clinicalServiceSteps} />
        </div>
        <div className="flex items-center gap-3 ">
          <Button
            type="primary-outline"
            text="Back"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={() => {
              onPrevious();
            }}
          />
          <Button
            type="primary"
            text="Next"
            className={`w-[200px] max-h-[52px] !rounded-[30px] ${
              !isNextEnabled() &&
              clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis &&
              "opacity-40 pointer-events-none"
            }`}
            disabled={
              !isNextEnabled() &&
              clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis
            }
            onClick={() => {
              if (
                isNextEnabled() ||
                clinicalServiceName !== ClinicalServiceTypes.AllergicRhinitis
              )
                onNext();
            }}
          />
        </div>
      </div>
      {showVaccineBookingPopup && (
        <VaccineBookingPopup
          selectedVaccinesForAppt={selectedVaccinesForAppt}
          comingFromEdit={comingFromEdit}
          originalReduxVaccines={clinicalServiceObj.initialAssessment.initialAssessmentForm.education?.vaccineSchedule || []}
          onSave={(updatedVaccines) => {
            setShowVaccineBookingPopup(false);

            const consistentVaccines = normalizeVaccines(updatedVaccines).map((v) => ({
              ...v,
              bookingDate: v.bookingDate?.includes("T") ? v.bookingDate : ensureConsistentDateFormat(v.bookingDate) || "",
            }));

            setSelectedVaccinesForAppt(consistentVaccines);
            setApptBookedVaccines(consistentVaccines);
            setVaccineResultCard(consistentVaccines.length > 0);
            setVaccineApptBooked(consistentVaccines.length > 0);
            if (consistentVaccines.length > 0) setVaccineSuppression(false);
            persistVaccinesLocally(consistentVaccines);

            justSavedVaccinesRef.current = true;

            dispatchUpdate({
              education: buildEducationPayload({
                vaccineSchedule: consistentVaccines,
                appointmentBooked: consistentVaccines.length > 0,
              }),
              currentStep, currentSubStep, serviceSteps: clinicalServiceSteps,
            });

            setTimeout(() => (justSavedVaccinesRef.current = false), 1000);
          }}
          onClose={() => {
            setShowVaccineBookingPopup(false);
            setVaccineResultCard(false);
          }}
        />
      )}
    </div>
  );
};

export default FollowUp;
